/**
 * ResizableHeader — Column header with drag-to-reorder and drag-to-resize.
 *
 * - @dnd-kit/sortable's useSortable enables pointer drag for column reorder
 * - Resize handle on the right border triggers resize on pointerdown (mouse + touch)
 * - 16px invisible touch target around the 3px visible resize line
 * - Ctrl+Left/Ctrl+Right keyboard shortcut for accessible column reorder
 * - Optional column menu (Sort/Move/Hide) via columnMenuProps
 * - Column reorder (dnd-kit) disabled on touch devices; resize always enabled
 *
 * Note: Drop indicator is rendered at the table level (single element)
 * rather than per-column to prevent flickering during drag operations.
 */

import { useSortable } from '@dnd-kit/sortable';
import React, { useCallback } from 'react';
import type { SortDescriptor } from 'react-aria-components';

import { cx } from '@/utils/cx';

import { styles } from '../styles';
import { ColumnHeaderMenuTrigger } from './column-header-menu';
import { SortableHeader } from './sortable-header';

export interface ColumnMenuProps {
  sortDirection: 'asc' | 'desc' | null;
  isFirst: boolean;
  isLast: boolean;
  canSort: boolean;
  onSort: (direction: 'asc' | 'desc') => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  onHide: () => void;
}

export interface ResizableHeaderProps {
  /** Column identifier used as @dnd-kit sortable ID */
  colId: string;
  /** Display label */
  label: string;
  /** Effective column width in pixels */
  width: number;
  /** Table size variant */
  size: keyof typeof styles.sizes;
  /** Whether this column supports click-to-sort */
  isSortable: boolean;
  /** Current sort state */
  sortDescriptor: SortDescriptor | null | undefined;
  /** Called when sort changes */
  onSortChange: (descriptor: SortDescriptor | null) => void;
  /** Called when resize starts (pointerdown on resize handle) */
  onResizeStart: (
    colId: string,
    startX: number,
    currentWidth: number,
    pointerId: number
  ) => void;
  /** Whether this column is actively being resized */
  isResizingThis: boolean;
  /** Called for keyboard column reorder — Ctrl+Left/Ctrl+Right */
  onKeyboardReorder?: (colId: string, direction: 'left' | 'right') => void;
  /** Called for keyboard column resize — Arrow keys on resize handle */
  onKeyboardResize?: (colId: string, delta: number) => void;
  /** Disable drag-to-reorder (touch devices). Resize handles remain active. */
  disabled?: boolean;
  /** Optional column menu props — when provided, renders a dropdown menu */
  columnMenuProps?: ColumnMenuProps;
}

/**
 * Column header with drag-to-reorder (dnd-kit), resize handle, and
 * Ctrl+Arrow keyboard reorder support.
 */
export function ResizableHeader({
  colId,
  label,
  width,
  size,
  isSortable: isSortableProp,
  sortDescriptor,
  onSortChange,
  onResizeStart,
  isResizingThis,
  onKeyboardReorder,
  onKeyboardResize,
  disabled = false,
  columnMenuProps,
}: ResizableHeaderProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: colId,
    disabled,
  });

  let ariaSort: 'ascending' | 'descending' | undefined;
  if (sortDescriptor?.column === colId) {
    ariaSort =
      sortDescriptor.direction === 'ascending' ? 'ascending' : 'descending';
  }

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Capture pointer so touch drag continues even if finger drifts off the handle
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      onResizeStart(colId, e.clientX, width, e.pointerId);
    },
    [colId, width, onResizeStart]
  );

  // Ctrl+Left/Right for keyboard-accessible column reorder.
  // Placed after {...listeners} spread so it always wins for Ctrl+Arrow keys.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!disabled && onKeyboardReorder && (e.ctrlKey || e.metaKey)) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          onKeyboardReorder(colId, 'left');
          return;
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          onKeyboardReorder(colId, 'right');
          return;
        }
      }
    },
    [colId, disabled, onKeyboardReorder]
  );

  // Static positioning — no transform applied. Columns stay in place during drag.
  // The DragOverlay shows the floating preview, drop indicator shows insertion point.
  const dragStyle: React.CSSProperties = {
    // Dimmer placeholder when dragging (overlay shows the actual content)
    opacity: isDragging ? 0.3 : 1,
    position: 'relative',
    width,
    minWidth: width,
  };

  return (
    <div
      ref={setNodeRef}
      style={dragStyle}
      className={cx(
        styles.common.headerCell,
        styles.sizes[size].headerCell,
        'p-0 group',
        !disabled && !isDragging && 'cursor-grab',
        isDragging && 'cursor-grabbing',
        // Dashed border placeholder when being dragged
        isDragging &&
          'border border-dashed border-[var(--color-border-primary)]'
      )}
      // @dnd-kit ARIA attributes (role, tabIndex, aria-describedby, etc.)
      {...attributes}
      // Override role — table semantics require columnheader, not button
      role='columnheader'
      aria-sort={ariaSort}
      // @dnd-kit pointer event listeners (onPointerDown etc.)
      {...listeners}
      // Our keyboard reorder handler — must come last to override listeners' onKeyDown
      onKeyDown={handleKeyDown}
    >
      {/* Header content with optional column menu trigger */}
      <div className='flex items-center flex-1 min-w-0'>
        {(() => {
          // When column menu is provided, just show label (menu trigger has the chevron)
          if (columnMenuProps) {
            return (
              <span className='truncate px-3 text-xs font-medium text-[var(--color-text-tertiary)]'>
                {label}
              </span>
            );
          }
          // Otherwise, use SortableHeader if sortable, or plain label
          if (isSortableProp) {
            return (
              <SortableHeader
                columnKey={colId}
                label={label}
                sortDescriptor={sortDescriptor ?? null}
                onSortChange={onSortChange}
              />
            );
          }
          return <span className='truncate px-3'>{label}</span>;
        })()}

        {/* Column menu trigger — single chevron for Sort/Move/Hide */}
        {columnMenuProps && (
          <ColumnHeaderMenuTrigger
            columnLabel={label}
            sortDirection={columnMenuProps.sortDirection}
            isFirst={columnMenuProps.isFirst}
            isLast={columnMenuProps.isLast}
            canSort={columnMenuProps.canSort}
            onSort={columnMenuProps.onSort}
            onMoveLeft={columnMenuProps.onMoveLeft}
            onMoveRight={columnMenuProps.onMoveRight}
            onHide={columnMenuProps.onHide}
          />
        )}
      </div>

      {/* Resize handle — right border drag target, centered on column edge.
           The outer div provides a wide 16px touch target; the inner div is the
           visible 3px line. touch-action:none prevents the browser from
           interpreting the drag as a scroll gesture. */}
      <div
        role='separator'
        aria-orientation='vertical'
        aria-label={`Resize ${label} column`}
        tabIndex={0}
        className='absolute top-0 bottom-0 z-10 flex items-stretch justify-center cursor-col-resize group/resize'
        style={{
          width: 16,
          right: 0,
          transform: 'translateX(50%)',
          touchAction: 'none',
        }}
        onPointerDown={handleResizePointerDown}
        onKeyDown={e => {
          if (!onKeyboardResize) {
            return;
          }
          const step = e.shiftKey ? 50 : 10;
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            e.stopPropagation();
            onKeyboardResize(colId, -step);
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            e.stopPropagation();
            onKeyboardResize(colId, step);
          }
        }}
        onClick={e => e.stopPropagation()}
      >
        <div
          className={cx(
            'w-[3px] transition-colors duration-75',
            'group-hover/resize:bg-[var(--color-brand-500)]',
            'group-focus-visible/resize:bg-[var(--color-brand-600)]',
            isResizingThis && 'bg-[var(--color-brand-600)]'
          )}
        />
      </div>
    </div>
  );
}
